// Angular Files
import {
 Component, OnInit, OnDestroy, Output, EventEmitter, Input
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

// Teller Online Library Files
import { TellerOnlineAppService } from 'teller-online-libraries/core';
import { HelpArticle, HelpTag } from '../../models';
import { HelpServiceBase } from '../../services';

@Component({
    selector: 'teller-online-help-sidebar',
    templateUrl: './help-sidebar.component.html',
    styleUrls: ['./help-sidebar.component.scss'],
    host: {
        class: 'help-sidebar'
    },
    standalone: false
})
export class TellerOnlineHelpSidebarComponent implements OnInit, OnDestroy {
    // Declare @Input variables
    @Input() public showViewAllLink: boolean = true;

    @Input() public viewAllRoute: string = '/help';

    // Declare @Output variables
    @Output() public toggleHelp = new EventEmitter<boolean>();

    // Public variables
    public relevantArticles: HelpArticle[];

    public relevantGroups: { group: string | null, articles: HelpArticle[] }[];

    public otherArticles: HelpArticle[];

    public totalArticleCount: number;

    public searchString: string;

    // Private variables
    private _tag: HelpTag;

    private _routerSubscription: Subscription;

    private _searchSubscription: Subscription;

    private _searchSubject = new Subject<string>();

    constructor(
        public appService: TellerOnlineAppService,
        public helpService: HelpServiceBase,
        private router: Router
    ) { }

    public ngOnInit() {
        this.totalArticleCount = this.helpService.articles.length;
        this._determineTagByPage();
        this._filterArticles(this._tag, true);

        // Subscribe to search input changes with debounce
        this._searchSubscription = this._searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe(value => {
            if (value) {
                this._filterArticles(value);
            } else {
                this._filterArticles(this._tag, true);
            }
        });

        // Subscribe to route changes to update the tag when navigating
        this._routerSubscription = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this._determineTagByPage();
                // Only re-filter if not currently searching
                if (!this.searchString) {
                    this._filterArticles(this._tag, true);
                }
            });
    }

    public ngOnDestroy() {
        this._routerSubscription?.unsubscribe();
        this._searchSubscription?.unsubscribe();
    }

    public onSearchInput(value: string) {
        this._searchSubject.next(value);
    }

    public onSubmit_searchArticles(e: Event) {
        e.preventDefault();
        // Immediate search on Enter, bypassing debounce
        if (this.searchString) {
            this._filterArticles(this.searchString);
        } else {
            this._filterArticles(this._tag, true);
        }
    }

    public onReset_clearSearch() {
        this.searchString = '';
        this._filterArticles(this._tag, true);
    }

    public onClick_toggleHelp = () => {
        this.toggleHelp.emit(true);
    };

    private _filterArticles(filterValue: string, tagMatch: boolean = false) {
        this.relevantArticles = this.helpService.articles.filter(article => article.match(filterValue, tagMatch));
        this.otherArticles = this.helpService.articles.filter(article => !article.match(filterValue, tagMatch));

        // Build grouped structure for relevant articles: ungrouped first, then each group
        this.relevantGroups = this._groupArticles(this.relevantArticles);

        // Only grab at most 3 articles if we pulled relevant articles, we'll link off to view the rest
        if (this.relevantArticles?.length > 0) this.otherArticles = this.otherArticles.slice(0, 2);
        // If we don't have any relevant articles, load at most 5 articles, we'll link off to view the rest
        else this.otherArticles = this.otherArticles.slice(0, 4);
    }

    private _groupArticles(articles: HelpArticle[]): { group: string | null, articles: HelpArticle[] }[] {
        const ungrouped = articles.filter(a => !a.group);
        const grouped = articles.filter(a => !!a.group);

        // Collect unique group names in the order they appear
        const groupNames: string[] = [];
        grouped.forEach(a => {
            if (!groupNames.includes(a.group!)) {
                groupNames.push(a.group!);
            }
        });

        const result: { group: string | null, articles: HelpArticle[] }[] = [];

        if (ungrouped.length > 0) {
            result.push({ group: null, articles: ungrouped });
        }

        groupNames.forEach(name => {
            result.push({ group: name, articles: grouped.filter(a => a.group === name) });
        });

        return result;
    }

    private _determineTagByPage() {
        // Delegate to the portal-specific help service to determine the tag
        this._tag = this.helpService.determineTagByUrl(this.appService.currentUrl);
    }
}
